import { useState, useEffect, useCallback } from "react";
import { Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ROWS = 13;

/* ─── Shared card style ──────────────────────────────────────────── */
const card = {
  background:   "var(--vz-secondary-bg, #fff)",
  border:       "1px solid var(--vz-border-color)",
  borderRadius: 10,
  boxShadow:    "0 1px 4px rgba(0,0,0,0.06)",
  overflow:     "hidden",
};

/* ─── Event meta ─────────────────────────────────────────────────── */
const EVENT_META = {
  LOGIN:            { label: "Login",            icon: "log-in",       color: "var(--vz-success)",  bg: "rgba(var(--vz-success-rgb),.12)",  border: "rgba(var(--vz-success-rgb),.3)",  dot: "#22c55e" },
  LOGOUT:           { label: "Logout",           icon: "log-out",      color: "var(--vz-danger)",   bg: "rgba(var(--vz-danger-rgb),.12)",   border: "rgba(var(--vz-danger-rgb),.3)",   dot: "#ef4444" },
  FAILED_LOGIN:     { label: "Failed Login",     icon: "alert-circle", color: "#f97316",            bg: "rgba(249,115,22,.12)",             border: "rgba(249,115,22,.3)",             dot: "#f97316" },
  USER_CREATED:     { label: "User Created",     icon: "user-plus",    color: "#0284c7",            bg: "rgba(2,132,199,.12)",              border: "rgba(2,132,199,.3)",              dot: "#0284c7" },
  USER_DELETED:     { label: "User Deleted",     icon: "user-minus",   color: "#7c3aed",            bg: "rgba(124,58,237,.12)",             border: "rgba(124,58,237,.3)",             dot: "#7c3aed" },
  PASSWORD_CHANGE:  { label: "Password Changed", icon: "key",          color: "#d97706",            bg: "rgba(217,119,6,.12)",              border: "rgba(217,119,6,.3)",              dot: "#d97706" },
  PAGE_VISIT:       { label: "Page Visit",       icon: "monitor",      color: "var(--vz-secondary-color)", bg: "rgba(var(--vz-secondary-rgb),.08)", border: "rgba(var(--vz-secondary-rgb),.2)", dot: "#94a3b8" },
  REPORT_DOWNLOAD:  { label: "Report Download",  icon: "download",     color: "#0891b2",            bg: "rgba(8,145,178,.12)",              border: "rgba(8,145,178,.3)",              dot: "#0891b2" },
  SETTINGS_CHANGED: { label: "Settings Changed", icon: "settings",     color: "#65a30d",            bg: "rgba(101,163,13,.12)",             border: "rgba(101,163,13,.3)",             dot: "#65a30d" },
};

const eventMeta = (type) => EVENT_META[type] || { label: type, icon: "activity", color: "var(--vz-secondary-color)", bg: "rgba(var(--vz-secondary-rgb),.08)", border: "var(--vz-border-color)", dot: "#94a3b8" };

/* ─── Filter groups ──────────────────────────────────────────────── */
const FILTER_GROUPS = [
  { val: "all",              label: "All" },
  { val: "login",            label: "Login",           color: "#16a34a" },
  { val: "logout",           label: "Logout",          color: "#dc2626" },
  { val: "failed_login",     label: "Failed Login",    color: "#f97316" },
  { val: "user_created",     label: "User Created",    color: "#0284c7" },
  { val: "user_deleted",     label: "User Deleted",    color: "#7c3aed" },
  { val: "password_change",  label: "Password Change", color: "#d97706" },
  { val: "page_visit",       label: "Page Visit",      color: "#64748b" },
  { val: "report_download",  label: "Report Download", color: "#0891b2" },
  { val: "settings_changed", label: "Settings",        color: "#65a30d" },
];

/* ─── Helpers ────────────────────────────────────────────────────── */
const fmtAgo = (ts) => {
  if (!ts) return "—";
  const diff = Math.floor((Date.now() - new Date(ts)) / 60000);
  if (diff < 1)     return "Just now";
  if (diff < 60)    return `${diff}m ago`;
  if (diff < 1440)  return `${Math.floor(diff / 60)}h ago`;
  if (diff < 10080) return `${Math.floor(diff / 1440)}d ago`;
  return new Date(ts).toLocaleDateString();
};

const fmtTs = (ts) => {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-US", {
    month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const getToken = () => {
  try {
    return JSON.parse(sessionStorage.getItem("authUser") || "{}")?.token || null;
  } catch { return null; }
};

/* ─── Users Modal ────────────────────────────────────────────────── */
const UsersModal = ({ users, logs, onClose }) => {
  const [search, setSearch] = useState("");

  const filtered = users.filter(u =>
    u.username.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1040, backdropFilter: "blur(2px)" }}
      />

      {/* Modal */}
      <div style={{
        position:     "fixed",
        top:          "50%",
        left:         "50%",
        transform:    "translate(-50%, -50%)",
        zIndex:       1050,
        width:        500,
        maxWidth:     "calc(100vw - 32px)",
        maxHeight:    "80vh",
        background:   "var(--vz-secondary-bg, #fff)",
        borderRadius: 12,
        border:       "1px solid var(--vz-border-color)",
        boxShadow:    "0 20px 60px rgba(0,0,0,.25)",
        display:      "flex",
        flexDirection:"column",
        animation:    "modalIn .2s ease",
      }}>
        <style>{`
          @keyframes modalIn {
            from { opacity: 0; transform: translate(-50%, -48%) scale(.97); }
            to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          }
        `}</style>

        {/* Header */}
        <div style={{ padding: "18px 20px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(2,132,199,.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <FeatherIcon icon="users" size={16} style={{ color: "#0284c7" }} />
            </div>
            <div>
              <h6 style={{ margin: 0, fontWeight: 700, fontSize: 15, color: "var(--vz-body-color)" }}>Active Users</h6>
              <p style={{ margin: 0, fontSize: 12, color: "var(--vz-secondary-color)" }}>{users.length} user{users.length !== 1 ? "s" : ""} with activity</p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", padding: 4 }}
            onMouseEnter={e => e.currentTarget.style.color = "var(--vz-danger)"}
            onMouseLeave={e => e.currentTarget.style.color = "var(--vz-secondary-color)"}
          >
            <FeatherIcon icon="x" size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--vz-border-color)", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{ width: "100%", paddingLeft: 30, paddingRight: 12, height: 34, border: "1px solid var(--vz-border-color)", borderRadius: 8, fontSize: 13, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>

        {/* User list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: "32px 20px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
              <FeatherIcon icon="search" size={28} style={{ opacity: .3, display: "block", margin: "0 auto 8px" }} />
              <p style={{ fontSize: 13, margin: 0 }}>No users match your search</p>
            </div>
          ) : (
            filtered.map((u, i) => {
              const userLogs   = logs.filter(l => l.email === u.email);
              const lastLog    = userLogs[0];
              const loginCount = userLogs.filter(l => l.event_type === "LOGIN").length;
              const failCount  = userLogs.filter(l => l.event_type === "FAILED_LOGIN").length;
              const initials   = (u.username || u.email || "?").slice(0, 2).toUpperCase();

              return (
                <div
                  key={i}
                  style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          12,
                    padding:      "10px 20px",
                    borderBottom: i < filtered.length - 1 ? "1px solid var(--vz-border-color)" : "none",
                    transition:   "background .15s",
                    cursor:       "default",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"}
                  onMouseLeave={e => e.currentTarget.style.background = ""}
                >
                  {/* Avatar */}
                  <div style={{
                    width:          38,
                    height:         38,
                    borderRadius:   "50%",
                    background:     "rgba(var(--vz-primary-rgb),.12)",
                    border:         "1px solid rgba(var(--vz-primary-rgb),.2)",
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    flexShrink:     0,
                    fontSize:       13,
                    fontWeight:     700,
                    color:          "var(--vz-primary)",
                  }}>
                    {initials}
                  </div>

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "var(--vz-body-color)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.username || "—"}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--vz-secondary-color)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {u.email}
                    </div>
                  </div>

                  {/* Stats */}
                  <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                    {loginCount > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "rgba(var(--vz-success-rgb),.12)", color: "var(--vz-success)", border: "1px solid rgba(var(--vz-success-rgb),.3)" }}>
                        {loginCount} login{loginCount !== 1 ? "s" : ""}
                      </span>
                    )}
                    {failCount > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: "rgba(249,115,22,.12)", color: "#f97316", border: "1px solid rgba(249,115,22,.3)" }}>
                        {failCount} failed
                      </span>
                    )}
                  </div>

                  {/* Last seen */}
                  <div style={{ fontSize: 11, color: "var(--vz-secondary-color)", flexShrink: 0, textAlign: "right" }}>
                    {lastLog ? fmtAgo(lastLog.logged_at) : "—"}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--vz-border-color)", flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{ height: 34, padding: "0 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", background: "var(--vz-primary)", color: "#fff", border: "none" }}
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
};

/* ─── Main component ─────────────────────────────────────────────── */
const UserActivityLogs = () => {
  const [logs,       setLogs]       = useState([]);
  const [filtered,   setFiltered]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [page,       setPage]       = useState(1);
  const [search,     setSearch]     = useState("");
  const [filter,     setFilter]     = useState("all");
  const [summary,    setSummary]    = useState({ total: 0, logins: 0, logouts: 0, failed: 0, downloads: 0, users: 0 });
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [uniqueUsers,    setUniqueUsers]    = useState([]);

  /* ── Fetch ── */
  const fetchLogs = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/auth/activity-logs?limit=200&offset=0`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Failed to load activity logs");
      const data  = await res.json();
      const list  = data.logs || [];

      // Build unique users list
      const userMap = {};
      list.forEach(l => {
        if (l.email && !userMap[l.email]) {
          userMap[l.email] = { username: l.username, email: l.email };
        }
      });
      const uList = Object.values(userMap).sort((a, b) =>
        (a.username || a.email).localeCompare(b.username || b.email)
      );

      const logins    = list.filter(l => l.event_type === "LOGIN").length;
      const logouts   = list.filter(l => l.event_type === "LOGOUT").length;
      const failed    = list.filter(l => l.event_type === "FAILED_LOGIN").length;
      const downloads = list.filter(l => l.event_type === "REPORT_DOWNLOAD").length;

      setLogs(list);
      setUniqueUsers(uList);
      setSummary({ total: list.length, logins, logouts, failed, downloads, users: uList.length });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  /* ── Filter + search ── */
  useEffect(() => {
    let f = logs;
    if (filter !== "all")
      f = f.filter(l => l.event_type === filter.toUpperCase().replace("-", "_"));
    if (search) {
      const t = search.toLowerCase();
      f = f.filter(l =>
        (l.username   || "").toLowerCase().includes(t) ||
        (l.email      || "").toLowerCase().includes(t) ||
        (l.ip_address || "").includes(t)
      );
    }
    setFiltered(f);
    setPage(1);
  }, [search, filter, logs]);

  const totalPages  = Math.ceil(filtered.length / ROWS);
  const currentRows = filtered.slice((page - 1) * ROWS, page * ROWS);

  /* ── Filter button ── */
  const filterBtn = (val, label, count, color) => {
    const active = filter === val;
    return (
      <button key={val} onClick={() => setFilter(val)} style={{
        padding: "5px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
        background: active ? (color || "var(--vz-primary)") : "transparent",
        color:      active ? "#fff" : "var(--vz-secondary-color)",
        border:     active ? `1px solid ${color || "var(--vz-primary)"}` : "1px solid var(--vz-border-color)",
        transition: "all .15s",
      }}>
        {label} <span style={{ opacity: .8 }}>{count}</span>
      </button>
    );
  };

  document.title = "User Activity Logs | SOC Platform";

  const summaryCards = [
    { label: "Total Events",     value: summary.total,     icon: "list",         color: "var(--vz-primary)", clickable: false },
    { label: "Logins",           value: summary.logins,    icon: "log-in",       color: "var(--vz-success)", clickable: false },
    { label: "Logouts",          value: summary.logouts,   icon: "log-out",      color: "var(--vz-danger)",  clickable: false },
    { label: "Failed Logins",    value: summary.failed,    icon: "alert-circle", color: "#f97316",           clickable: false },
    { label: "Report Downloads", value: summary.downloads, icon: "download",     color: "#0891b2",           clickable: false },
    { label: "Total Users",      value: summary.users,     icon: "users",        color: "#0284c7",           clickable: true  },
  ];

  return (
    <>
      <style>{`
        @keyframes spin    { to { transform: rotate(360deg); } }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>

      <div className="page-content">
        <div style={{ maxWidth: "100%", padding: "0 4px" }}>

          {/* ── Page title ── */}
          <div style={{ marginBottom: 20 }}>
            <h5 style={{ fontWeight: 700, color: "var(--vz-body-color)", margin: 0 }}>
              User Activity Logs
            </h5>
            <p style={{ fontSize: 13, color: "var(--vz-secondary-color)", margin: "4px 0 0" }}>
              Login and logout events for all users in your organisation
            </p>
          </div>

          {/* ── Summary cards ── */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 16 }}>
            {summaryCards.map((s) => (
              <div
                key={s.label}
                onClick={s.clickable ? () => setShowUsersModal(true) : undefined}
                style={{
                  ...card,
                  padding:    "14px 18px",
                  display:    "flex",
                  alignItems: "center",
                  gap:        14,
                  cursor:     s.clickable ? "pointer" : "default",
                  transition: "all .15s",
                  ...(s.clickable ? { border: "1px solid rgba(2,132,199,.25)" } : {}),
                }}
                onMouseEnter={e => {
                  if (s.clickable) {
                    e.currentTarget.style.background = "rgba(2,132,199,.04)";
                    e.currentTarget.style.borderColor = "rgba(2,132,199,.5)";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 12px rgba(2,132,199,.15)";
                  }
                }}
                onMouseLeave={e => {
                  if (s.clickable) {
                    e.currentTarget.style.background = "";
                    e.currentTarget.style.borderColor = "rgba(2,132,199,.25)";
                    e.currentTarget.style.transform = "";
                    e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)";
                  }
                }}
              >
                <div style={{
                  width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                  background: `color-mix(in srgb, ${s.color} 15%, transparent)`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  position: "relative",
                }}>
                  <FeatherIcon icon={s.icon} size={18} style={{ color: s.color }} />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 22, fontWeight: 800, color: "var(--vz-body-color)", margin: 0, lineHeight: 1 }}>
                    {loading ? "—" : s.value}
                  </p>
                  <p style={{ fontSize: 12, color: "var(--vz-secondary-color)", margin: "3px 0 0" }}>{s.label}</p>
                </div>
                {s.clickable && (
                  <div style={{ flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 600, color: "#0284c7", display: "flex", alignItems: "center", gap: 3 }}>
                      View <FeatherIcon icon="chevron-right" size={11} />
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* ── Error banner ── */}
          {error && (
            <div style={{ padding: "10px 16px", background: "rgba(var(--vz-danger-rgb),.1)", border: "1px solid rgba(var(--vz-danger-rgb),.3)", borderRadius: 8, fontSize: 13, color: "var(--vz-danger)", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
              <FeatherIcon icon="alert-circle" size={14} />{error}
            </div>
          )}

          {/* ── Filter bar ── */}
          <div style={{ ...card, marginBottom: 12, padding: "12px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
              <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 320 }}>
                <FeatherIcon icon="search" size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--vz-secondary-color)", pointerEvents: "none" }} />
                <input
                  type="text"
                  placeholder="Search by username, email or IP..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{ width: "100%", paddingLeft: 30, paddingRight: search ? 30 : 12, height: 34, border: "1px solid var(--vz-border-color)", borderRadius: 8, fontSize: 13, color: "var(--vz-body-color)", background: "var(--vz-card-bg, #fff)", outline: "none" }}
                />
                {search && (
                  <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--vz-secondary-color)", padding: 0 }}>✕</button>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {FILTER_GROUPS.map(f =>
                  filterBtn(f.val, f.label,
                    f.val === "all" ? summary.total : logs.filter(l => l.event_type === f.val.toUpperCase().replace("-","_")).length,
                    f.color
                  )
                )}
              </div>
            </div>
          </div>

          {/* ── Table card ── */}
          <div style={card}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: "var(--vz-body-color)" }}>Activity Log</span>
                <span style={{ fontSize: 12, padding: "2px 10px", background: "rgba(var(--vz-primary-rgb),.1)", borderRadius: 20, color: "var(--vz-primary)", fontWeight: 600 }}>
                  {filtered.length}
                </span>
              </div>
              <button
                onClick={fetchLogs}
                disabled={loading}
                style={{ height: 32, padding: "0 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer", background: "transparent", color: "var(--vz-primary)", border: "1px solid var(--vz-primary)", display: "flex", alignItems: "center", gap: 4 }}
              >
                <FeatherIcon icon="refresh-cw" size={13} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <Spinner color="primary" />
                <p style={{ marginTop: 8, color: "var(--vz-secondary-color)", fontSize: 13 }}>Loading activity logs...</p>
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["#", "User", "Email", "Event", "Details", "IP Address", "Time", ""].map((h, i) => (
                          <th key={i} style={{ padding: "10px 18px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--vz-secondary-color)", textAlign: "left", borderBottom: "1px solid var(--vz-border-color)", background: "var(--vz-light, rgba(0,0,0,.02))", whiteSpace: "nowrap" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {currentRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ padding: "48px 24px", textAlign: "center", color: "var(--vz-secondary-color)" }}>
                            <FeatherIcon icon="inbox" size={36} className="d-block mx-auto mb-2" />
                            {search || filter !== "all" ? "No logs match your filters" : "No activity logs found"}
                            {(search || filter !== "all") && (
                              <button onClick={() => { setSearch(""); setFilter("all"); }}
                                style={{ display: "block", margin: "10px auto 0", background: "none", border: "none", color: "var(--vz-primary)", fontWeight: 600, cursor: "pointer", textDecoration: "underline", fontSize: 13 }}>
                                Clear filters
                              </button>
                            )}
                          </td>
                        </tr>
                      ) : currentRows.map((log, idx) => {
                        const meta = eventMeta(log.event_type);
                        return (
                          <tr
                            key={log.id}
                            style={{ borderBottom: "1px solid var(--vz-border-color)", transition: "background .15s" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "rgba(var(--vz-primary-rgb),.03)"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = ""; }}
                          >
                            <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)", width: 48 }}>
                              {(page - 1) * ROWS + idx + 1}
                            </td>
                            <td style={{ padding: "12px 18px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{ width: 32, height: 32, borderRadius: "50%", background: meta.bg, border: `1px solid ${meta.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                  <FeatherIcon icon="user" size={14} style={{ color: meta.color }} />
                                </div>
                                <div>
                                  <span style={{ fontWeight: 600, fontSize: 13, color: "var(--vz-body-color)", display: "block" }}>
                                    {log.username || "—"}
                                  </span>
                                  <span style={{ fontSize: 10, color: "var(--vz-secondary-color)" }}>
                                    {fmtAgo(log.logged_at)}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: "12px 18px", fontSize: 13, color: "var(--vz-secondary-color)" }}>
                              {log.email || "—"}
                            </td>
                            <td style={{ padding: "12px 18px" }}>
                              <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 20, background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
                                <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.dot }} />
                                {meta.label}
                              </span>
                            </td>
                            <td style={{ padding: "12px 18px", maxWidth: 220 }}>
                              {log.details ? (() => {
                                const d = typeof log.details === "string" ? JSON.parse(log.details) : log.details;
                                const entries = Object.entries(d).filter(([,v]) => v !== null && v !== undefined && v !== "");
                                if (entries.length === 0) return <span style={{ fontSize: 11, opacity: .4 }}>—</span>;
                                return (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    {entries.slice(0, 3).map(([k, v]) => (
                                      <span key={k} style={{ fontSize: 11, color: "var(--vz-secondary-color)" }}>
                                        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{k.replace(/_/g, " ")}:</span>{" "}
                                        <span style={{ fontFamily: typeof v === "boolean" ? "inherit" : "monospace" }}>{String(v)}</span>
                                      </span>
                                    ))}
                                  </div>
                                );
                              })() : <span style={{ fontSize: 11, opacity: .4 }}>—</span>}
                            </td>
                            <td style={{ padding: "12px 18px" }}>
                              {log.ip_address ? (
                                <code style={{ fontSize: 12, color: "#0284c7", background: "rgba(2,132,199,.1)", padding: "2px 8px", borderRadius: 4 }}>
                                  {log.ip_address}
                                </code>
                              ) : (
                                <span style={{ fontSize: 11, opacity: .45 }}>—</span>
                              )}
                            </td>
                            <td style={{ padding: "12px 18px", fontSize: 12, color: "var(--vz-secondary-color)", whiteSpace: "nowrap" }}>
                              {fmtTs(log.logged_at)}
                            </td>
                            <td style={{ padding: "12px 18px" }}>
                              <FeatherIcon icon="chevron-right" size={15} style={{ color: "var(--vz-border-color)" }} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div style={{ padding: "12px 18px", borderTop: "1px solid var(--vz-border-color)", display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 13 }}>
                    <span style={{ color: "var(--vz-secondary-color)" }}>
                      Showing {(page - 1) * ROWS + 1}–{Math.min(page * ROWS, filtered.length)} of {filtered.length}
                    </span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
                        style={{ height: 30, padding: "0 10px", borderRadius: 6, border: "1px solid var(--vz-border-color)", background: "transparent", color: page === 1 ? "var(--vz-secondary-color)" : "var(--vz-body-color)", cursor: page === 1 ? "not-allowed" : "pointer", opacity: page === 1 ? .4 : 1, display: "flex", alignItems: "center", gap: 4 }}>
                        <FeatherIcon icon="chevron-left" size={13} /> Prev
                      </button>
                      <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                        style={{ height: 30, padding: "0 10px", borderRadius: 6, border: "1px solid var(--vz-border-color)", background: "transparent", color: page >= totalPages ? "var(--vz-secondary-color)" : "var(--vz-body-color)", cursor: page >= totalPages ? "not-allowed" : "pointer", opacity: page >= totalPages ? .4 : 1, display: "flex", alignItems: "center", gap: 4 }}>
                        Next <FeatherIcon icon="chevron-right" size={13} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

        </div>
      </div>

      {/* ── Users Modal ── */}
      {showUsersModal && (
        <UsersModal
          users={uniqueUsers}
          logs={logs}
          onClose={() => setShowUsersModal(false)}
        />
      )}
    </>
  );
};

export default UserActivityLogs;